// @flow
import { startRequest, finishRequest } from "@store/utils";
import {
  ExportRTWDetailsReport,
  ExportRTWDetailsReportSuccess,
  ExportRTWDetailsReportError,
  GetReportingRTWDetailsDefaultReports,
  GetReportingRTWDetailsDefaultReportsSuccess,
  GetReportingRTWDetailsDefaultReportsError,
  GetReportingRTWDetailsMyReports,
  GetReportingRTWDetailsMyReportsSuccess,
  GetReportingRTWDetailsMyReportsError,
  GetReportingRTWDetailsReport,
  GetReportingRTWDetailsReportSuccess,
  GetReportingRTWDetailsReportError,
  GetReportingRTWDetailsChartsSuccess,
  GetReportingRTWDetailsCharts,
  GetReportingRTWDetailsChartsError,
  GetRTWDetailsFiltersSchema,
  GetRTWDetailsFiltersSchemaSuccess,
  GetRTWDetailsFiltersSchemaError,
  SetRTWDetailsReportToSave,
  SetRtwDetailsReportViewState,
  ToggleRtwInterviewModal,
  GetReportingRTWDetailsCurrentReport,
  GetRTWDetailsReportAbsenceRTWInterview,
  GetRTWDetailsReportAbsenceRTWInterviewSuccess,
} from "@modules/reporting/constants/actions";
import type { RtwDetailsState } from "./types";
import { cloneObject } from "@utils/object";

export const initialState: RtwDetailsState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  mySavedReports: [],
  currentReport: null,
  currentCharts: null,
  reportData: {
    content: [],
    pagination: {
      sort: { orders: [] },
      pageNumber: 0,
      pageSize: 0,
      offset: 0,
    },
    totalElements: 0,
  },
  rtwDetailsFiltersSchema: null,
  reportToSave: null,
  viewState: {
    filtersExpanded: false,
    viewChartsExpanded: true,
    viewReportExpanded: true,
  },
  modals: {
    rtwInterview: {
      visible: false,
      rtwTask: null,
    },
  },
};

export const setLoading = (state: RtwDetailsState) => () => startRequest(state);

export const onError = (state: RtwDetailsState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetReportingRTWDetailsReport = (state: RtwDetailsState) => ({ payload }: Object) => {
  return {
    ...state,
    currentReport: payload,
  };
};

export const onSetRTWDetailsReportToSave = (state: RtwDetailsState) => ({ payload }: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onSetRtwDetailsReportViewState = (state: RtwDetailsState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onToggleRtwInterviewModal = (state: RtwDetailsState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      rtwInterview: payload,
    },
  };
};

export const onGetReportingRTWDetailsDefaultReportsSuccess = (state: RtwDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetReportingRTWDetailsReportSuccess = (state: RtwDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    reportData: payload,
  };
};

export const onGetReportingRTWDetailsChartsSuccess = (state: RtwDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    currentCharts: payload,
  };
};

export const onGetReportingRTWDetailsMySavedReportsSuccess = (state: RtwDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    mySavedReports: payload,
  };
};

export const onGetRTWDetailsFiltersSchemaSuccess = (state: RtwDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    rtwDetailsFiltersSchema: payload,
  };
};

export const onExportRTWDetailsReport = (state: RtwDetailsState) => () => {
  return {
    ...state,
    reportGenerateInProgress: ++state.reportGenerateInProgress,
  };
};

export const onExportRTWDetailsReportLoaded = (state: RtwDetailsState) => () => {
  return {
    ...state,
    reportGenerateInProgress:
      state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
  };
};

export const onGetRTWDetailsReportAbsenceRTWInterviewSuccess = (state: RtwDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    modals: {
      ...state.modals,
      rtwInterview: {
        visible: true,
        rtwTask: payload,
      },
    },
  };
};

export default (state: RtwDetailsState = initialState, action: Object): RtwDetailsState => {
  const act = {
    [ExportRTWDetailsReport]: onExportRTWDetailsReport,
    [ExportRTWDetailsReportSuccess]: onExportRTWDetailsReportLoaded,
    [ExportRTWDetailsReportError]: onExportRTWDetailsReportLoaded,
    [GetReportingRTWDetailsDefaultReportsSuccess]: onGetReportingRTWDetailsDefaultReportsSuccess,
    [GetReportingRTWDetailsCurrentReport]: onGetReportingRTWDetailsReport,
    [GetReportingRTWDetailsReportSuccess]: onGetReportingRTWDetailsReportSuccess,
    [GetReportingRTWDetailsMyReportsSuccess]: onGetReportingRTWDetailsMySavedReportsSuccess,
    [GetReportingRTWDetailsChartsSuccess]: onGetReportingRTWDetailsChartsSuccess,
    [GetRTWDetailsFiltersSchemaSuccess]: onGetRTWDetailsFiltersSchemaSuccess,
    [SetRTWDetailsReportToSave]: onSetRTWDetailsReportToSave,
    [SetRtwDetailsReportViewState]: onSetRtwDetailsReportViewState,
    [ToggleRtwInterviewModal]: onToggleRtwInterviewModal,
    [GetRTWDetailsReportAbsenceRTWInterviewSuccess]: onGetRTWDetailsReportAbsenceRTWInterviewSuccess,
    [GetReportingRTWDetailsDefaultReports]: setLoading,
    [GetReportingRTWDetailsReport]: setLoading,
    [GetReportingRTWDetailsMyReports]: setLoading,
    [GetReportingRTWDetailsCharts]: setLoading,
    [GetRTWDetailsFiltersSchema]: setLoading,
    [GetRTWDetailsReportAbsenceRTWInterview]: setLoading,
    [GetReportingRTWDetailsDefaultReportsError]: onError,
    [GetReportingRTWDetailsReportError]: onError,
    [GetReportingRTWDetailsMyReportsError]: onError,
    [GetReportingRTWDetailsChartsError]: onError,
    [GetRTWDetailsFiltersSchemaError]: onError,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
